import { useState, useCallback, useRef, useMemo } from 'react';
import { Checkbox, Card, CardContent, CardHeader, CardTitle, toast } from '@aaas/shared';
import { Loader2, CheckCircle, XCircle } from 'lucide-react';
import { useAccessMatrixQuery, useBulkUpdateAccessMutation } from '../hooks';
import { UserSelector } from './UserSelector';
import { SpaceSearchFilter } from './SpaceSearchFilter';

// Simple debounce utility
function debounce<T extends (...args: unknown[]) => unknown>(func: T, wait: number): T {
  let timeout: NodeJS.Timeout | null = null;
  return ((...args: Parameters<T>) => {
    if (timeout) clearTimeout(timeout);
    timeout = setTimeout(() => func(...args), wait);
  }) as T;
}

interface AccessMatrixProps {
  integrationId: string;
}

export function AccessMatrix({ integrationId }: AccessMatrixProps) {
  // User and space selection state
  const [selectedUserIds, setSelectedUserIds] = useState<number[]>([]);
  const [selectedSpaceIds, setSelectedSpaceIds] = useState<string[]>([]);

  // Fetch all users (no pagination needed with user selection)
  const { data, isLoading, error } = useAccessMatrixQuery(integrationId, 1, 1000);
  const bulkUpdateMutation = useBulkUpdateAccessMutation(integrationId);

  // Track pending changes
  const pendingChangesRef = useRef<Map<string, boolean>>(new Map());

  // Debounced save function
  const saveChanges = useCallback(
    debounce(() => {
      const changes = Array.from(pendingChangesRef.current.entries()).map(([key, grant]) => {
        const [, userId, , spaceId] = key.split('_');
        return {
          user_id: parseInt(userId),
          space_id: spaceId,
          grant,
        };
      });

      if (changes.length > 0) {
        bulkUpdateMutation.mutate(changes, {
          onSuccess: () => {
            // Show success toast with details
            const grantedChanges = changes.filter((c) => c.grant);
            const revokedChanges = changes.filter((c) => !c.grant);

            const users = data?.users || [];
            const spaces = data?.spaces || [];

            if (grantedChanges.length > 0) {
              grantedChanges.forEach((change) => {
                const user = users.find((u) => u.id === change.user_id);
                const space = spaces.find((p) => p.id === change.space_id);

                if (user && space) {
                  const message = `Granted ${user.display_name || user.email} access to ${space.name}`;
                  toast.success(message, {
                    icon: <CheckCircle className="h-5 w-5 text-green-500" />,
                    style: {
                      border: '1px solid #10b981',
                      backgroundColor: '#f0fdf4',
                    },
                  });
                }
              });
            }

            if (revokedChanges.length > 0) {
              revokedChanges.forEach((change) => {
                const user = users.find((u) => u.id === change.user_id);
                const space = spaces.find((p) => p.id === change.space_id);

                if (user && space) {
                  const message = `Revoked ${user.display_name || user.email} access to ${space.name}`;
                  toast.success(message, {
                    icon: <XCircle className="h-5 w-5 text-orange-500" />,
                    style: {
                      border: '1px solid #f97316',
                      backgroundColor: '#fff7ed',
                    },
                  });
                }
              });
            }

            pendingChangesRef.current.clear();
          },
          onError: (error) => {
            const errorMessage = `Failed to update access: ${error instanceof Error ? error.message : 'Unknown error'}`;
            console.error('Error updating access:', errorMessage);
            toast.error(errorMessage);
          },
        });
      }
    }, 1000),
    [integrationId, data]
  );

  const handleCheckboxChange = (userId: number, spaceId: string, checked: boolean) => {
    const key = `user_${userId}_space_${spaceId}`;
    pendingChangesRef.current.set(key, checked);
    saveChanges();
  };

  // Filter users: show selected users OR all if none selected
  const displayUsers = useMemo(() => {
    if (selectedUserIds.length === 0) return data?.users || [];
    return data?.users.filter((u) => selectedUserIds.includes(u.id)) || [];
  }, [data?.users, selectedUserIds]);

  // Filter spaces: show selected spaces OR all if none selected
  const displaySpaces = useMemo(() => {
    if (selectedSpaceIds.length === 0) return data?.spaces || [];
    return data?.spaces.filter((p) => selectedSpaceIds.includes(p.id)) || [];
  }, [data?.spaces, selectedSpaceIds]);

  if (isLoading) {
    return (
      <Card>
        <CardContent className="flex items-center justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card>
        <CardContent className="py-12">
          <div className="text-center text-destructive">
            Error loading access matrix: {error instanceof Error ? error.message : 'Unknown error'}
          </div>
        </CardContent>
      </Card>
    );
  }

  if (!data || data.users.length === 0) {
    return (
      <Card>
        <CardContent className="py-12">
          <div className="text-center text-muted-foreground">
            No Slack users found. Users will appear here after they interact with the bot.
          </div>
        </CardContent>
      </Card>
    );
  }

  if (data.spaces.length === 0) {
    return (
      <Card>
        <CardContent className="py-12">
          <div className="text-center text-muted-foreground">
            No spaces available. Create a space first to manage user access.
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Access Control Matrix</CardTitle>
        <p className="text-sm text-muted-foreground">
          Select users to manage their space access. Changes are saved automatically.
        </p>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* User selector */}
        <UserSelector
          users={data?.users || []}
          selectedUserIds={selectedUserIds}
          onSelectionChange={setSelectedUserIds}
        />

        {/* Space selector */}
        <SpaceSearchFilter
          spaces={data?.spaces || []}
          selectedSpaceIds={selectedSpaceIds}
          onSelectionChange={setSelectedSpaceIds}
        />

        {/* Matrix table */}
        <div className="overflow-x-auto">
          <table className="w-full border-collapse">
            <thead>
              <tr>
                <th className="sticky left-0 z-10 bg-background border border-border p-3 text-left font-medium min-w-[200px]">
                  Space
                </th>
                {displayUsers.map((user) => (
                  <th
                    key={user.id}
                    className="border border-border p-3 font-medium min-w-[200px] max-w-[250px]"
                  >
                    <div className="flex items-center gap-2 justify-center">
                      {user.avatar_url && (
                        <img
                          src={user.avatar_url}
                          alt={user.display_name || user.email}
                          className="w-8 h-8 rounded-full object-cover flex-shrink-0"
                        />
                      )}
                      <div className="flex flex-col min-w-0 text-left">
                        <div
                          className="font-medium truncate"
                          title={user.display_name || user.email}
                        >
                          {user.display_name || user.email.split('@')[0]}
                        </div>
                        <div className="text-xs text-muted-foreground truncate" title={user.email}>
                          {user.email}
                        </div>
                      </div>
                    </div>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {displaySpaces.map((space) => (
                <tr key={space.id} className="hover:bg-muted/50">
                  <td className="sticky left-0 z-10 bg-background border border-border p-3 font-medium">
                    <div className="truncate" title={space.name}>
                      {space.name}
                    </div>
                  </td>
                  {displayUsers.map((user) => {
                    const key = `user_${user.id}_space_${space.id}`;
                    const hasAccess = data.access_matrix[key] || false;

                    return (
                      <td key={key} className="border border-border p-3">
                        <div className="flex items-center justify-center h-full">
                          <Checkbox
                            checked={hasAccess}
                            onCheckedChange={(checked) =>
                              handleCheckboxChange(user.id, space.id, checked === true)
                            }
                            disabled={bulkUpdateMutation.isPending}
                            aria-label={`${hasAccess ? 'Revoke' : 'Grant'} ${user.display_name || user.email} access to ${space.name}`}
                          />
                        </div>
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Footer summary */}
        <div className="flex items-center justify-between text-sm text-muted-foreground pt-4 border-t">
          <div>
            Showing {displayUsers.length} user{displayUsers.length !== 1 ? 's' : ''} •{' '}
            {displaySpaces.length} space{displaySpaces.length !== 1 ? 's' : ''}
          </div>
          <div className="flex gap-4">
            {selectedUserIds.length > 0 && (
              <span>
                {selectedUserIds.length} of {data.users.length} users selected
              </span>
            )}
            {selectedSpaceIds.length > 0 && (
              <span>
                {selectedSpaceIds.length} of {data.spaces.length} spaces selected
              </span>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
